import { useQueries } from "@tanstack/react-query";

import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { getQueryParams } from "app/pages/industry-analysis/common/getQueryParams";
import type { IUniqueUsersOvertimeRawData } from "app/pages/industry-analysis/overview/utils/types";
import { createMetricFromPopData } from "app/pages/industry-analysis/overview/widgets/high-level-metrics/comparison/utils/dataProcessor";
import { DefaultFetchService } from "app/services/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TRAFFIC_AND_ENGAGEMENT_ENDPOINTS, UNIQUE_USERS_ENDPOINTS } from "../consts";
import type {
    CategoryComparisonData,
    CategoryPeriodData,
    HighLevelMetricsComparisonChartData,
    HighLevelMetricsComparisonChartDataWithPreviousPeriod,
    MetricGraphData,
    MonthlyMetricData,
    PeriodOverPeriodDataPoint,
    PeriodOverPeriodTrafficAndEngagementData,
    StructuredHighLevelMetricsData,
    TrafficAndEngagementData,
} from "../types";

import { useDurationValidationRecovery } from "./useDurationValidationRecovery";

/**
 * Aligns previous period data to use current period dates for proper overlay visualization.
 * Maps previous period values to current period dates by index position.
 *
 * @param currentData - Current period data with the target date structure
 * @param previousData - Previous period data with values to map
 * @returns Previous data with current period dates
 */
const alignPreviousPeriodDates = (
    currentData: MonthlyMetricData,
    previousData: { Monthly?: MetricGraphData } | MetricGraphData,
) => {
    // Handle both formats: direct Monthly structure or nested Monthly structure
    const previousGraph =
        "Monthly" in previousData && previousData.Monthly
            ? previousData.Monthly.Graph
            : "Graph" in previousData
              ? previousData.Graph
              : [];

    const previousAverage =
        "Monthly" in previousData && previousData.Monthly
            ? previousData.Monthly.Average
            : "Average" in previousData
              ? previousData.Average
              : 0;

    // Map previous period values to current period dates by index
    const alignedGraph = currentData.Monthly.Graph.map((currentPoint, index) => ({
        Key: currentPoint.Key, // Use current period date
        Value: previousGraph[index]?.Value || 0,
        // Preserve original previous period date for tooltips
        originalPreviousDate: previousGraph[index]?.Key,
    }));

    return {
        Monthly: {
            Graph: alignedGraph,
            Average: previousAverage,
        },
        Weekly: { Graph: [], Average: 0 },
        Daily: { Graph: [], Average: 0 },
    };
};

const buildCategoryQueryParams = (category: ICategoryComparison["category"]) => {
    const baseQueryParams = getQueryParams();

    return {
        ...baseQueryParams,
        keys: category.forApi,
        category: category.categoryId,
        categoryHash: category.isCustomCategory ? category.categoryHash : "",
    };
};

const shouldFetchUniqueUsers = (
    category: ICategoryComparison["category"],
    queryParams: ReturnType<typeof getQueryParams>,
): boolean => {
    return (!category.isCustomCategory || category.segments?.length === 0) && !queryParams.isWindow;
};

const processPeriodOverPeriodData = (
    category: ICategoryComparison["category"],
    trafficData: PeriodOverPeriodTrafficAndEngagementData,
    uniqueUsersData?: IUniqueUsersOvertimeRawData,
): HighLevelMetricsComparisonChartData => {
    const popMetricsData = trafficData.Graph.Total;

    const normalizedMetrics = {
        // Current period data
        Visits: createMetricFromPopData(popMetricsData.Visits, "current"),
        BounceRate: createMetricFromPopData(popMetricsData.BounceRate, "current"),
        PagesPerVisit: createMetricFromPopData(popMetricsData.PagesPerVisit, "current"),
        VisitDuration: createMetricFromPopData(popMetricsData.VisitDuration, "current"),
        // Previous period data (for internal use)
        VisitsPrevious: createMetricFromPopData(popMetricsData.Visits, "previous"),
        BounceRatePrevious: createMetricFromPopData(popMetricsData.BounceRate, "previous"),
        PagesPerVisitPrevious: createMetricFromPopData(popMetricsData.PagesPerVisit, "previous"),
        VisitDurationPrevious: createMetricFromPopData(popMetricsData.VisitDuration, "previous"),
    };

    // Combine the data
    const result = {
        id: category.id,
        name: category.text || category.name,
        color: "", // Will be set by later by the calling hook
        ...normalizedMetrics,
    } as HighLevelMetricsComparisonChartData;

    // Add unique users data to the result
    if (uniqueUsersData?.Data && Object.keys(uniqueUsersData.Data).length > 0) {
        const uniqueUsersMetrics = Object.values(uniqueUsersData.Data)[0]?.["Total"] as PeriodOverPeriodDataPoint[];

        if (uniqueUsersMetrics) {
            result.UniqueUsers = {
                Monthly: createMetricFromPopData(uniqueUsersMetrics, "current").Monthly,
            };

            // Add previous period unique users data for POP mode
            (
                result as HighLevelMetricsComparisonChartData & {
                    UniqueUsersPrevious: MetricGraphData;
                }
            ).UniqueUsersPrevious = createMetricFromPopData(uniqueUsersMetrics, "previous").Monthly;
        }
    }

    return result;
};

const processRegularData = (
    category: ICategoryComparison["category"],
    trafficData: TrafficAndEngagementData,
    uniqueUsersData?: IUniqueUsersOvertimeRawData,
): HighLevelMetricsComparisonChartData => {
    const metricsData = trafficData.Graph.Total;

    const result = {
        id: category.id,
        name: category.text || category.name,
        color: "", // Will be set later by the calling hook
        ...metricsData,
    } as HighLevelMetricsComparisonChartData;

    // Add unique users data to the result
    if (uniqueUsersData?.Data && Object.keys(uniqueUsersData.Data).length > 0) {
        const uniqueUsersMetrics = Object.values(uniqueUsersData.Data)[0]?.["Total"];

        if (uniqueUsersMetrics) {
            result.UniqueUsers = { Monthly: { Graph: uniqueUsersMetrics?.[0], Average: 0 } };
        }
    }

    return result;
};

/**
 * Fetches high-level metrics data for a single category, supporting both regular and period-over-period modes
 */
const fetchCategoryHighLevelMetrics = async (
    category: ICategoryComparison["category"],
): Promise<HighLevelMetricsComparisonChartData> => {
    const params = proNavigator.getParams();
    const isPeriodOverPeriod = !!params.comparedDuration;
    const fetchService = DefaultFetchService.getInstance();

    const queryParams = buildCategoryQueryParams(category);

    // Select appropriate traffic endpoint based on POP mode
    const trafficEndpoint = isPeriodOverPeriod
        ? TRAFFIC_AND_ENGAGEMENT_ENDPOINTS.OVERVIEW_COMPARE
        : TRAFFIC_AND_ENGAGEMENT_ENDPOINTS.OVERVIEW;

    // Check if we need unique users data
    const shouldFetchUniqueData = shouldFetchUniqueUsers(category, queryParams);

    if (!shouldFetchUniqueData) {
        if (isPeriodOverPeriod) {
            const trafficData = await fetchService.get<PeriodOverPeriodTrafficAndEngagementData>(
                trafficEndpoint,
                queryParams,
            );

            if (!trafficData || !trafficData.Graph || !trafficData.Graph.Total) {
                return;
            }

            return processPeriodOverPeriodData(category, trafficData);
        } else {
            const trafficData = await fetchService.get<TrafficAndEngagementData>(trafficEndpoint, queryParams);

            if (!trafficData || !trafficData.Graph || !trafficData.Graph.Total) {
                return;
            }

            return processRegularData(category, trafficData);
        }
    }

    // Fetch unique users data
    const uniqueUsersQueryParams = {
        ...queryParams,
        timeGranularity: toggleItems.Monthly.value,
    };

    const uniqueUsersEndpoint = isPeriodOverPeriod
        ? UNIQUE_USERS_ENDPOINTS.OVERTIME_PERIOD_OVER_PERIOD
        : UNIQUE_USERS_ENDPOINTS.OVERTIME;

    if (isPeriodOverPeriod) {
        const [trafficData, uniqueUsersData] = await Promise.all([
            fetchService.get<PeriodOverPeriodTrafficAndEngagementData>(trafficEndpoint, queryParams),
            fetchService.get<IUniqueUsersOvertimeRawData>(uniqueUsersEndpoint, uniqueUsersQueryParams),
        ]);

        return processPeriodOverPeriodData(category, trafficData, uniqueUsersData);
    } else {
        const [trafficData, uniqueUsersData] = await Promise.all([
            fetchService.get<TrafficAndEngagementData>(trafficEndpoint, queryParams),
            fetchService.get<IUniqueUsersOvertimeRawData>(uniqueUsersEndpoint, uniqueUsersQueryParams),
        ]);

        return processRegularData(category, trafficData, uniqueUsersData);
    }
};

export const useHighLevelMetricsComparisonData = (categories: ICategoryComparison[]) => {
    const params = proNavigator.getParams();
    const { duration, country, webSource, comparedDuration, includeSubdomains } = params;
    const isPeriodOverPeriod = !!comparedDuration;

    const queryResult = useQueries({
        queries: categories.map(({ category, id }) => ({
            queryKey: ["highLevelMetrics", id, { duration, country, webSource, comparedDuration, includeSubdomains }],
            queryFn: async (): Promise<CategoryComparisonData> => {
                const result = await fetchCategoryHighLevelMetrics(category);

                const currentPeriodData: CategoryPeriodData = {
                    id: result.id,
                    name: result.name,
                    metrics: {
                        Visits: result.Visits,
                        BounceRate: result.BounceRate,
                        PagesPerVisit: result.PagesPerVisit,
                        VisitDuration: result.VisitDuration,
                        UniqueUsers: result.UniqueUsers,
                    },
                };

                if (!isPeriodOverPeriod) {
                    // Regular mode: return just current period
                    return {
                        category: currentPeriodData,
                    };
                }

                const resultWithPreviousPeriod = result as HighLevelMetricsComparisonChartDataWithPreviousPeriod;

                const previousPeriodData: CategoryPeriodData = {
                    id: result.id,
                    name: result.name,
                    // Remove color from cached data - it should come from current context
                    metrics: {
                        Visits: alignPreviousPeriodDates(
                            result.Visits,
                            resultWithPreviousPeriod.VisitsPrevious || { Monthly: { Graph: [], Average: 0 } },
                        ),
                        BounceRate: alignPreviousPeriodDates(
                            result.BounceRate,
                            resultWithPreviousPeriod.BounceRatePrevious || { Monthly: { Graph: [], Average: 0 } },
                        ),
                        PagesPerVisit: alignPreviousPeriodDates(
                            result.PagesPerVisit,
                            resultWithPreviousPeriod.PagesPerVisitPrevious || { Monthly: { Graph: [], Average: 0 } },
                        ),
                        VisitDuration: alignPreviousPeriodDates(
                            result.VisitDuration,
                            resultWithPreviousPeriod.VisitDurationPrevious || { Monthly: { Graph: [], Average: 0 } },
                        ),
                        UniqueUsers: resultWithPreviousPeriod.UniqueUsersPrevious
                            ? alignPreviousPeriodDates(result.UniqueUsers || { Monthly: { Graph: [], Average: 0 } }, {
                                  Graph: resultWithPreviousPeriod.UniqueUsersPrevious.Graph,
                                  Average: resultWithPreviousPeriod.UniqueUsersPrevious.Average,
                              })
                            : ({ Monthly: { Graph: [], Average: 0 } } as MonthlyMetricData),
                    },
                };

                return {
                    category: currentPeriodData,
                    previousPeriod: previousPeriodData,
                };
            },
        })),
        combine: (
            results,
        ): StructuredHighLevelMetricsData & {
            isLoading: boolean;
            isError: boolean;
            isSuccess: boolean;
            error: unknown;
        } => {
            const isLoading = results.some((result) => result.isLoading);
            const isError = results.some((result) => result.isError);
            const isSuccess = results.every((result) => result.isSuccess);

            return {
                categoriesData: results.map((result) => result.data).filter(Boolean),
                isPeriodOverPeriod,
                isLoading,
                isError,
                isSuccess,
                error: results.find((result) => result.isError)?.error || null,
            };
        },
    });

    const { isRecovering } = useDurationValidationRecovery(queryResult.error, queryResult.isSuccess);

    return {
        ...queryResult,
        isLoading: queryResult.isLoading || isRecovering,
    };
};
